import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import TableContainer from '../../components/Common/TableContainer';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation, useQueryClient } from 'react-query';

//import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
//Import Flatepicker
import 'flatpickr/dist/themes/material_blue.css';
import Flatpickr from 'react-flatpickr';

import {
  addNewClaim as onAddNewClaim,
  updateClaim as onUpdateClaim,
  setClaimSummary,
} from 'store/actions';

import { PaymentStatus } from './ClaimCol';

//redux
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import ClaimsModal from './ClaimModal';

import {
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
} from 'reactstrap';
import moment from 'moment';
import { useAuth } from 'hooks/useAuth';
import { getSearchColumns, sumArray } from 'utils/tools';
import { ToastContainer } from 'react-toastify';
import { getAllClaimDisbursementResultsApi } from 'api/transaction';
import { getClaimStatusCountsApi } from 'api/report';
import { useRole } from 'hooks/useRole';
import MainFilter from 'components/Common/MainFilter';
import StatCards from 'components/Common/StatCards';

function ClaimDisbursementsCallbacks() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuth();
  const [pages, setPages] = useState(0);
  const queryClient = useQueryClient();
  const {
    isFacilitator,
    isSiteCord,
    isCountyCoordinator,
    isClusterLeader,
    isFinance,
    isDirector,
    isAdmin,
    isRegionalCountyCoordinator,
    isSuperAdmin,
    isData,
    addresses,
  } = useRole();
  const { wardId, constituencyId, countyId, regionId } = isAuthenticated
    ? addresses[0]
    : {};
  const [addressData, setAddressData] = useState({});

  const { claimSummary } = useSelector((state) => ({
    claimSummary: state.reports.claims,
  }));

  const claimsCardData = [
    {
      title: 'Total Claims',
      iconClass: 'bx bx-user',
      description: claimSummary?.length
        ? sumArray(claimSummary, 'totalClaims')
        : 0,
    },
    {
      title: 'Approved',
      iconClass: 'bx bx-check-circle',
      description: claimSummary?.length
        ? sumArray(claimSummary, 'totalProcessed')
        : 0,
    },
    {
      title: 'Processing',
      iconClass: 'bx bx-pause-circle',
      description: claimSummary?.length
        ? sumArray(claimSummary, 'totalPending')
        : 0,
    },
    {
      title: 'Rejected',
      iconClass: 'bx bx-error-circle',
      description: claimSummary?.length
        ? sumArray(claimSummary, 'totalDeclined')
        : 0,
    },
  ];
  const dateFormat = 'DD-MM-YYYY';
  const [pagination, setPagination] = useState({
    pageSize: 10,
    pageNumber: 1,
    sortBy: 'id',
    ascendingSort: false,
    wardIds: getSearchColumns()?.find((t) => t?.name === 'wardIds')?.value,
    searchColumns: [
      ...(isFacilitator
        ? [
          {
            name: 'wardId',
            value: wardId,
          },
        ]
        : []),
      ...(isCountyCoordinator
        ? [
          {
            name: 'countyId',
            value: countyId,
          },
          {
            name: 'countyIds',
            values: addresses?.map((c) => c.countyId),
          },
        ]
        : []),
      ...(isRegionalCountyCoordinator
        ? [
          {
            name: 'regionId',
            value: regionId,
          },
          {
            name: 'regionIds',
            values: addresses?.map((c) => c.regionId),
          },
        ]
        : []),
      ...(isClusterLeader
        ? [
          {
            name: 'regionId',
            value: regionId,
          },
          {
            name: 'constituencyIds',
            values: addresses?.map((c) => c.constituencyId),
          },
        ]
        : []),
      ...(isSiteCord
        ? [
          {
            name: 'constituencyId',
            value: constituencyId,
          },
          {
            name: 'constituencyIds',
            values: addresses?.map((c) => c.constituencyId),
          },
        ]
        : []),
      {
        name: 'endDate',
        value: moment().format(dateFormat),
      },
      {
        name: 'startDate',
        value: moment().startOf('year').format(dateFormat),
      },
    ],
  });

  //meta title
  document.title = 'Claims Disbursements | SUN Welfare Administration System';

  const [modal, setModal] = useState(false);
  const [modal1, setModal1] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [member, setClaim] = useState(null);

  const { mutate: mutateClaimCounts, data } = useMutation(
    (payload) => getClaimStatusCountsApi(payload),
    {
      onSuccess: (res) => {
        dispatch(setClaimSummary(res?.data));
      },
      onSettled: () => {
        queryClient.invalidateQueries('get-all-counties');
      },
    },
  );

  const {
    mutate: mutateClaimDisbursements,
    isLoading: isLoadingDisbursements,
    data: disbursementsData,
  } = useMutation((payload) => getAllClaimDisbursementResultsApi(payload), {
    onSuccess: (res) => {
      res.pages > 0 && setPages(res.pages);
    },
    onSettled: () => {
      queryClient.invalidateQueries('get-all-counties');
    },
  });

  // validation
  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      fullNames: (member && member.fullNames) || '',
      reportedDate: (member && member.reportedDate) || '',
      groupName: (member && member.groupName) || '',
      deceasedName: (member && member.deceasedName) || '',
      status: (member && member.status) || 'Paid',
      badgeclass: (member && member.badgeclass) || 'success',
      memberStatus: (member && member.memberStatus) || 'Mastercard',
    },
    validationSchema: Yup.object({
      fullNames: Yup.string()
        .matches(/[0-9\.\-\s+\/()]+/, 'Please Enter Valid Claim Id')
        .required('Please Enter Your Claim Id'),
      reportedDate: Yup.string().required('Please Enter Your Billing Name'),
      groupName: Yup.string().required('Please Enter Your Claim Date'),
      deceasedName: Yup.string()
        .matches(/[0-9\.\-\s+\/()]+/, 'Please Enter Valid Amount')
        .required('Total Amount'),
      status: Yup.string().required('Please Enter Your Payment Status'),
      badgeclass: Yup.string().required('Please Enter Your Badge Class'),
      memberStatus: Yup.string().required('Please Enter Your Payment Method'),
    }),
    onSubmit: (values) => {
      if (isEdit) {
        const updateClaim = {
          id: member ? member.id : 0,
          fullNames: values.fullNames,
          reportedDate: values.reportedDate,
          groupName: values.groupName,
          deceasedName: values.deceasedName,
          status: values.status,
          memberStatus: values.memberStatus,
          badgeclass: values.badgeclass,
        };
        // update member
        dispatch(onUpdateClaim(updateClaim));
        validation.resetForm();
      } else {
        const newClaim = {
          id: Math.floor(Math.random() * (30 - 20)) + 20,
          fullNames: values['fullNames'],
          reportedDate: values['reportedDate'],
          groupName: values['groupName'],
          deceasedName: values['deceasedName'],
          status: values['status'],
          memberStatus: values['memberStatus'],
          badgeclass: values['badgeclass'],
        };
        // save new member
        dispatch(onAddNewClaim(newClaim));
        validation.resetForm();
      }
      toggle();
    },
  });

  const toggleViewModal = () => setModal1(!modal1);

  const selectClaimState = (state) => state.members;
  const ClaimProperties = createSelector(selectClaimState, (Claim) => ({
    members: generateFakeData(),
    // TO-DO return this for live data
    // members: Claim.members,
    loading: Claim.loading,
  }));

  useEffect(() => {
    mutateClaimCounts({
      fromDate: pagination.searchColumns?.find((r) => r.name === 'startDate')
        ?.value,
      toDate: moment().format('DD-MM-YYYY'),
      address: addresses[0],
      wardIds: pagination.wardIds,
      countyIds: pagination.searchColumns.find(s => s.name === 'countyIds')?.values,
      regionIds: pagination.searchColumns.find(s => s.name === 'regionIds')?.values,
      constituencyIds: pagination.searchColumns.find(s => s.name === 'constituencyIds')?.values,
    });
  }, []);

  useEffect(() => {

    // Fetch all claims
    mutateClaimDisbursements({
      ...pagination,
      pageNumber: pagination.pageNumber - 1,
    });

  }, [pagination]);

  const onChangePagination = ({ page, limit }, filters, sorter) => {
    console.log(filters);
    let searchColumns = isEmpty(filters)
      ? pagination.searchColumns
      : [
        ...pagination.searchColumns,
        ...(filters?.id
          ? [
            {
              name: 'id',
              value: filters?.id,
            },
          ]
          : []),
        ...(filters?.conversationID
          ? [
            {
              name: 'conversationID',
              value: filters?.conversationID,
            },
          ]
          : []),
        ...(filters?.originatorConversationId
          ? [
            {
              name: 'originatorConversationId',
              value: filters?.originatorConversationId,
            },
          ]
          : []),
        ...(filters?.email
          ? [
            {
              name: 'email',
              value: filters?.email,
            },
          ]
          : []),
        ...(filters?.claimNumber
          ? [
            {
              name: 'claimNumber',
              value: filters?.claimNumber,
            },
          ]
          : []),
        ...(filters?.mobileNumber1
          ? [
            {
              name: 'mobileNumber1',
              value: filters?.mobileNumber1,
            },
          ]
          : []),
      ];
    setPagination({
      ...pagination,
      pageNumber: page || pagination.pageNumber,
      pageSize: limit || pagination.pageSize,
      searchColumns,
    });
  };

  const toggle = () => {
    if (modal) {
      setModal(false);
      setClaim(null);
    } else {
      setModal(true);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: () => (
          <div className="form-check font-size-16">
            <input className="form-check-input" type="checkbox" id="checkAll" />
            <label className="form-check-label" htmlFor="checkAll"></label>
          </div>
        ),
        accessor: '#',
        width: '20px',
        filterable: true,
        Cell: (cellProps) => (
          <div className="form-check font-size-16">
            <input className="form-check-input" type="checkbox" id="checkAll" />
            <label className="form-check-label" htmlFor="checkAll"></label>
          </div>
        ),
      },
      {
        Header: 'ID',
        accessor: 'id',
        filterable: true,
        Cell: (cellProps) => {
          return <span>{cellProps.value}</span>;
        },
      },
      {
        Header: 'RECEIVER NAME',
        accessor: 'receiver',
        filterable: true,
        Cell: (cellProps) => {
          return <span>{cellProps?.value}</span>;
        },
      },
      {
        Header: 'REPORTED DATE',
        accessor: 'createdDate',
        filterable: true,
        Cell: (cellProps) => {
          return (
            <span>{moment(cellProps.value).format('YYYY MMM DD h:mm A')}</span>
          );
        },
      },
      {
        Header: 'Result Code',
        accessor: 'resultCode',
        filterable: true,
        Cell: (cellProps) => {
          return <span>{cellProps.value}</span>;
        },
      },
      {
        Header: 'RECEIPT NUMBER',
        accessor: "transactionID",
        filterable: true,
        Cell: (cellProps) => {
          return <span>{cellProps.value}</span>;
        },
      },
      {
        Header: 'STATUS',
        accessor: 'transactionStatus',
        Cell: (cellProps) => {
          return <PaymentStatus {...cellProps} />;
        },
      },
      {
        Header: 'TRANSACTION AMOUNT',
        accessor: 'transAmount',
        Cell: (cellProps) => {
          return <span>{cellProps.value}</span>;
        },
      },
      {
        Header: 'ConversationID',
        accessor: 'conversationID',
        Cell: (cellProps) => {
          return <span>{cellProps.value}</span>;
        },
      },
      {
        Header: 'OriginatorID',
        accessor: 'originatorConversationId',
        Cell: (cellProps) => {
          return <span>{cellProps.value}</span>;
        },
      },
    ],
    [],
  );

  const onSearch = (data) => {
    const searchColumns = [];
    if (!isEmpty(data)) {
      if (isFacilitator) {
        searchColumns.push({ name: 'wardId', value: address?.wardId });
      }
      if (isSiteCord && data.wardId) {
        searchColumns.push(data?.wardId);
      } else {
        if (
          (isClusterLeader ||
            isFinance ||
            isDirector ||
            isAdmin ||
            isSuperAdmin) &&
          data?.regionId
        ) {
          searchColumns.push(data?.regionId);
        }
        if (data.wardId) {
          searchColumns.push(data?.wardId);
        }
        if (data.constituencyId) {
          searchColumns.push(data?.constituencyId);
        }
        if (data.countyId) {
          searchColumns.push(data?.countyId);
        }
      }
      data.startDate && searchColumns.push(data.startDate);
      data.endDate && searchColumns.push(data.endDate);
    }

    setPagination({ ...pagination, searchColumns });

    let add = {};
    searchColumns.forEach((a, i) => {
      add[a.name] = a.value;
    });
    setAddressData(add);
  };

  return (
    <React.Fragment>
      <ClaimsModal isOpen={modal1} toggle={toggleViewModal} />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Home" breadcrumbItem="Claims" />
          <StatCards items={claimsCardData} md="3" />
          <Card>
            <CardBody className="pb-0">
              <MainFilter
                inlineActions
                // hasExport
                pagination={pagination}
                onSearch={onSearch}
                showDateFilter
              />
            </CardBody>
          </Card>
          <div>
            <TableContainer
              pageSizeAtBottom
              columns={columns}
              data={disbursementsData?.list || []}
              isGlobalFilter={true}
              isAddOptions={true}
              hasCreate={isFacilitator}
              isPagination={true}
              filterable={false}
              iscustomPageSizeOptions={true}
              hasSearch
              pages={pages}
              // showView
              loading={isLoadingDisbursements}
              resource="claim"
              addText="Create New Claim"
              tableClass="align-middle table-nowrap table-check"
              theadClass="table-light"
              pagination="pagination pagination-rounded justify-content-end mb-2"
              paginationData={pagination}
              onChangePagination={onChangePagination}
              searchByNames={[
                { value: 'claimNumber', text: t('claims.claimNumber') },
                { value: 'email', text: t('common.email') },
                { value: 'idNumber', text: t('common.idNumber') },
                { value: 'mobileNumber1', text: t('common.mobileNumber') },
                { value: 'conversationID', text: 'Conversation ID' },
                { value: 'originatorConversationId', text: 'Originator Conversation ID' },
              ]}
            />
          </div>
        </div>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
}
ClaimDisbursementsCallbacks.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default ClaimDisbursementsCallbacks;
